import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw, Save } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/hooks/use-mobile";

interface FormActionButtonsProps {
  /** Whether the form has unsaved changes */
  isDirty: boolean;
  /** Callback to save changes */
  onSave: () => void | Promise<void>;
  /** Callback to discard changes */
  onDiscard: () => void | Promise<void>;
  /** Whether currently saving */
  saving?: boolean;
  /** Custom save button text */
  saveText?: string;
  /** Custom discard button text */
  discardText?: string;
  /** Whether to hide text on mobile */
  hideTextOnMobile?: boolean;
}

/**
 * Reusable form action buttons for save and discard operations
 *
 * @example
 * ```tsx
 * <FormActionButtons
 *   isDirty={form.formState.isDirty}
 *   onSave={save}
 *   onDiscard={discard}
 *   saving={saving}
 * />
 * ```
 */
export function FormActionButtons({
  isDirty,
  onSave,
  onDiscard,
  saving = false,
  saveText,
  discardText,
  hideTextOnMobile = true,
}: FormActionButtonsProps) {
  const { t } = useTranslation("services");
  const isMobile = useIsMobile();

  const defaultSaveText = saveText || t("saveChanges");
  const defaultDiscardText = discardText || t("actions.discard");
  const savingText = t("actions.saving");

  return (
    <AnimatePresence>
      {isDirty && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95, x: 20 }}
          animate={{ opacity: 1, scale: 1, x: 0 }}
          exit={{ opacity: 0, scale: 0.95, x: 20 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="flex items-center gap-2"
        >
          <Button
            variant="outline"
            size="sm"
            onClick={onDiscard}
            className="flex items-center gap-1.5"
          >
            <RotateCcw className="w-4 h-4" />
            <AnimatePresence mode="wait">
              <motion.span
                key={hideTextOnMobile && isMobile ? "icon-only" : "with-text"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {hideTextOnMobile && isMobile ? "" : defaultDiscardText}
              </motion.span>
            </AnimatePresence>
          </Button>
          <Button
            size="sm"
            onClick={onSave}
            disabled={saving}
            className="flex items-center gap-1.5"
          >
            <Save className="w-4 h-4" />
            <AnimatePresence mode="wait">
              <motion.span
                key={saving ? "saving" : hideTextOnMobile && isMobile ? "icon-only" : "save-text"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {saving ? savingText : hideTextOnMobile && isMobile ? "" : defaultSaveText}
              </motion.span>
            </AnimatePresence>
          </Button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
